import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { formatStoredMoney } from "../utils/currency";
import { API_BASE } from "../config/constants";

const BillingHistoryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [nextDue, setNextDue] = useState(null);
  const [planType, setPlanType] = useState("");
  const [restaurantId, setRestaurantId] = useState(null);

  const fetchHistory = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const stored = JSON.parse(localStorage.getItem("userData") || "{}");
      const data = stored?.data || {};
      const id = data?.id;
      const validUpto = data?.validupto || data?.valid_upto || null;
      const pkg = String(data?.package_type || "").trim().toLowerCase();
      setRestaurantId(id || null);
      setNextDue(validUpto);
      setPlanType(pkg);
      if (!id) {
        setItems([]);
        return;
      }
      const res = await fetch(`${API_BASE}/billing/history`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: id, status: "SUCCESS" }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.message || "Failed to fetch");
      setItems(Array.isArray(result?.items) ? result.items : []);
    } catch (error) {
      toast.error(error?.message || "Unable to load billing history");
    } finally {
      if (!silent) setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const summary = useMemo(() => {
    const totalAmount = items.reduce((sum, row) => sum + Number(row?.amount || 0), 0);
    return {
      count: items.length,
      totalAmount,
      dueLabel: nextDue ? formatDateTime(nextDue) : "-",
    };
  }, [items, nextDue]);

  const fmtINR = formatStoredMoney;

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Hero>
        <HeroContent>
          <BadgeRow>
            <Badge>Billing</Badge>
            <PlanPill>Plan: {planType ? titleCase(planType) : "-"}</PlanPill>
          </BadgeRow>
          <Title>Billing History</Title>
          <Sub>Track your plan payments, renewals, and upcoming dues at a glance.</Sub>
        </HeroContent>
        <Actions>
          <PrimaryBtn onClick={() => fetchHistory(true)}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
        </Actions>
      </Hero>

      <StatsGrid>
        <StatCard>
          <StatLabel>Total Payments</StatLabel>
          <StatValue>{summary.count}</StatValue>
          <StatHint>Transactions so far</StatHint>
        </StatCard>
        <StatCard>
          <StatLabel>Total Paid</StatLabel>
          <StatValue>{fmtINR(summary.totalAmount)}</StatValue>
          <StatHint>Across all plans</StatHint>
        </StatCard>
        <StatCard>
          <StatLabel>Next Due</StatLabel>
          <StatValue>{summary.dueLabel}</StatValue>
          <StatHint>Renewal date</StatHint>
        </StatCard>
      </StatsGrid>

      <Card>
        <SectionHead>
          <div>
            <SectionTitle>Transactions</SectionTitle>
            <SectionSub>{items.length} records</SectionSub>
          </div>
          <SectionTag>Latest first</SectionTag>
        </SectionHead>

        {loading ? (
          <Center>Loading…</Center>
        ) : items.length === 0 ? (
          <EmptyState>No billing records found.</EmptyState>
        ) : (
          <Timeline>
            {items.map((item, idx) => {
              const status = String(item?.status || "").toUpperCase();
              return (
                <Item key={item?.id || idx}>
                  <ItemHead>
                    <div>
                      <ItemTitle>{titleCase(item?.plan_key)}</ItemTitle>
                      <ItemSub>{item?.duration_label || item?.duration_key || "-"}</ItemSub>
                    </div>
                    <AmountBlock>
                      <ItemAmount>{fmtINR(item?.amount)}</ItemAmount>
                      <Status $ok={status === "SUCCESS"}>{status || "-"}</Status>
                    </AmountBlock>
                  </ItemHead>
                  <MetaGrid>
                    <div>
                      <small>Order ID</small>
                      <strong>{item?.merchant_transaction_id || "-"}</strong>
                    </div>
                    <div>
                      <small>Paid On</small>
                      <strong>{formatDateTime(item?.created_at)}</strong>
                    </div>
                    <div>
                      <small>Gateway</small>
                      <strong>{item?.pg_name || "Online"}</strong>
                    </div>
                  </MetaGrid>
                </Item>
              );
            })}
          </Timeline>
        )}
      </Card>

      {!restaurantId && <Hint>Please login to see billing history.</Hint>}
    </Page>
  );
};

export default BillingHistoryPage;

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const titleCase = (value) => {
  if (!value) return "-";
  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const Page = styled.div`
  min-height: 100%;
  padding: 12px;
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, rgba(1, 91, 154, 1), rgba(0, 57, 98, 1));
  color: #fff;
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 10px 24px rgba(1, 91, 154, 0.18);
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const HeroContent = styled.div`
  display: grid;
  gap: 6px;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.25);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-size: 12px;
`;

const PlanPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-weight: 700;
  font-size: 12px;
`;

const Title = styled.h1`
  margin: 8px 0 4px;
  font-size: 1.6rem;
  font-weight: 900;
`;

const Sub = styled.div`
  opacity: 0.9;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PrimaryBtn = styled.button`
  background: #fff;
  color: #1d4ed8;
  border: 0;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const StatCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);
  display: grid;
  gap: 6px;
`;

const StatLabel = styled.div`
  color: #64748b;
  font-weight: 700;
  font-size: 0.9rem;
`;

const StatValue = styled.div`
  font-size: 1.4rem;
  font-weight: 900;
  color: #0f172a;
`;

const StatHint = styled.div`
  color: #94a3b8;
  font-size: 0.85rem;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.06);
`;

const SectionHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
`;

const SectionSub = styled.div`
  color: #64748b;
  font-size: 0.9rem;
`;

const SectionTag = styled.div`
  background: #eef2ff;
  color: #1d4ed8;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
`;

const Center = styled.div`
  text-align: center;
  color: #64748b;
  padding: 20px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #94a3b8;
  padding: 24px 0;
  font-weight: 600;
`;

const Timeline = styled.div`
  display: grid;
  gap: 12px;
`;

const Item = styled.div`
  border: 1px solid #e5e7eb;
  border-left: 4px solid #2563eb;
  border-radius: 14px;
  padding: 14px;
  background: #f8fafc;
`;

const ItemHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
`;

const ItemTitle = styled.div`
  font-weight: 800;
  font-size: 1rem;
`;

const ItemSub = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 4px;
`;

const AmountBlock = styled.div`
  text-align: right;
  display: grid;
  gap: 6px;
  justify-items: end;
`;

const Status = styled.span`
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  background: ${(p) => (p.$ok ? "#dcfce7" : "#e2e8f0")};
  color: ${(p) => (p.$ok ? "#166534" : "#334155")};
`;

const ItemAmount = styled.div`
  font-size: 1.2rem;
  font-weight: 900;
  margin-top: 6px;
`;

const MetaGrid = styled.div`
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
  small {
    color: #64748b;
    display: block;
  }
  strong {
    display: block;
    margin-top: 3px;
  }
`;

const Hint = styled.div`
  color: #94a3b8;
  font-size: 0.9rem;
`;
